// Display formatting, plain values and search text for typed cells.
// Dates are calendar dates ('YYYY-MM-DD'); they are formatted in UTC so no timezone shifts the day.

// The consuming page's product locale: an explicit locale wins, then the document's language, then the
// browser's.
export const productLocale = locale => {
  if (typeof locale === 'string' && locale) return locale
  if (typeof document !== 'undefined') {
    const lang = document.documentElement && document.documentElement.lang
    if (lang) return lang
  }
  return (typeof navigator !== 'undefined' && navigator.language) || 'en-US'
}

// The default date style; a column may declare its own Intl.DateTimeFormat options as `dateStyle`.
export const DEFAULT_DATE_STYLE = {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
}

const formatters = {}

const cached = (key, make) => {
  if (!formatters[key]) formatters[key] = make()
  return formatters[key]
}

const dateFormatter = (locale, style) =>
  cached(
    `date:${locale}:${JSON.stringify(style)}`,
    () => new Intl.DateTimeFormat(locale, { ...style, timeZone: 'UTC' })
  )

const numberFormatter = (locale, format) =>
  cached(
    `number:${locale}:${format || 'plain'}`,
    () =>
      new Intl.NumberFormat(
        locale,
        format === 'currency'
          ? { style: 'currency', currency: 'USD' }
          : { maximumFractionDigits: 20 }
      )
  )

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/

// A real calendar date in ISO form, as UTC milliseconds; anything else (2026-02-31 included) is null.
export const parseIsoDate = value => {
  if (typeof value !== 'string') return null
  const match = ISO_DATE.exec(value)
  if (!match) return null
  const [year, month, day] = [match[1], match[2], match[3]].map(Number)
  const time = Date.UTC(year, month - 1, day)
  const date = new Date(time)
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  )
    return null
  return time
}

const styleOf = (style, column) =>
  style || (column && column.dateStyle) || DEFAULT_DATE_STYLE

export const formatDate = (value, locale, style) => {
  const time = parseIsoDate(value)
  return time === null
    ? ''
    : dateFormatter(productLocale(locale), styleOf(style)).format(time)
}

export const isNumber = value =>
  typeof value === 'number' && Number.isFinite(value)

export const formatNumber = (value, format, locale) =>
  isNumber(value)
    ? numberFormatter(productLocale(locale), format).format(value)
    : ''

// The locale's group separator, found by formatting a number that needs one ("1,000" -> ",").
const groupSeparator = locale =>
  cached(`group:${locale}`, () => {
    const part = new Intl.NumberFormat(locale)
      .formatToParts(1000)
      .find(p => p.type === 'group')
    return part ? part.value : ''
  })

const LABELLED_TYPES = ['badge', 'link', 'button', 'split']

// A structured cell value ({label, href}, {label, family}) reduces to the text it shows.
export const plainValue = value => {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    if (value.label !== undefined) return value.label
    if (value.text !== undefined) return value.text
    return null
  }
  return value
}

// The plain value a column exposes to search, filters, sort and summaries: a declared value first, else the
// field, with links, buttons and badges reduced to their visible label.
export const cellValue = (column, row) => {
  if (column.value) return column.value(row)
  const value = row ? row[column.key] : undefined
  if (LABELLED_TYPES.includes(column.type)) {
    const plain = plainValue(value)
    return plain === undefined || plain === '' ? null : plain
  }
  return value
}

// The noninteractive value an export writes for one cell.
export const exportValue = (column, row) => {
  const declared =
    column.capabilities && column.capabilities.export
      ? column.capabilities.export(row)
      : cellValue(column, row)
  const plain = plainValue(declared)
  return plain === undefined ? null : plain
}

// Every string a search term may match for one cell.
export const searchTexts = (column, row, locale) => {
  const loc = productLocale(locale)
  if (column.searchText) return [column.searchText(row)].filter(Boolean)
  const value = cellValue(column, row)
  if (value === null || value === undefined || value === '') return []
  if (column.type === 'date') {
    return [formatDate(value, loc, styleOf(null, column)), String(value)]
  }
  if (column.type === 'numeric') {
    if (!isNumber(value)) return []
    const rendered = formatNumber(value, column.format, loc)
    const group = groupSeparator(loc)
    // drop group separators only; keep the decimal separator, sign and affixes
    return [String(value), group ? rendered.split(group).join('') : rendered]
  }
  return [String(value)]
}

export const displayText = (column, row, locale) => {
  const value = cellValue(column, row)
  if (column.type === 'date')
    return formatDate(value, locale, styleOf(null, column))
  if (column.type === 'numeric')
    return formatNumber(value, column.format, locale)
  return value === null || value === undefined ? '' : String(value)
}
